// ═══════════════════════════════════════
// NAVIGATION — Éditeur : chargement, sauvegarde, droits
// ═══════════════════════════════════════

import {
    sessionToken, currentPartitionMeta, currentId,
    setCurrentId, setCurrentSlug, setCurrentPartitionMeta,
    setPendingAction, setHeaderFormActive
} from './state.js';
import { authHeaders, openUserModal } from './common/auth.js';
import { highlightAll, getAbcContent } from './editor.js';
import { render, synthControl } from './renderer.js';
import { stopDrones } from './drone.js';
import { parseHeadersFromAbc, getPendingTuneId, clearPendingTuneId } from './header-form.js';
import { refreshAttachButtons, uploadAttachments } from './attachments.js';
import { generateAndSaveOgImage, generateAndStorePdf } from './export.js';
import { historyReset } from './history.js';
import { t, lang } from './i18n.js';
import { trackUsage } from './analytics.js';
import { markClean } from './autosave.js';

// ── refreshEditorAccess ───────────────────────────────────────────────────────
export function refreshEditorAccess() {
    const canEdit = sessionToken && (currentPartitionMeta?.is_owner ?? false);
    document.querySelectorAll('.abc-voice[contenteditable]').forEach(el => {
        el.contentEditable = canEdit ? 'true' : 'false';
    });
    updateOwnerUI();
}

// ── Initialisation depuis les données injectées par le serveur ────────────────
export async function initFromServerData() {
    const data = window.__PARTITION__;
    if (!data) return;
    await _loadPartitionIntoEditor(data);
}

// ── Charger une partition dans l'éditeur ──────────────────────────────────────
async function _loadPartitionIntoEditor(data) {
    setCurrentId(data.id);
    setCurrentSlug(data.slug);
    // is_owner : droit d'éditer (propriétaire, ou penn pupitre sur une partition
    // de son ensemble). owns : vrai propriétaire (suppression, visibilité, cession).
    setCurrentPartitionMeta({ is_owner: data.is_owner, owns: data.owns ?? data.is_owner, is_public: data.is_public });

    document.getElementById('partition-title-display').textContent = data.title;
    parseHeadersFromAbc(data.content);
    historyReset(data.content);
    highlightAll();
    document.getElementById('save-status').textContent = '';
    refreshEditorAccess();

    render();
    refreshAttachButtons(data.id);
    updateOwnerUI();

    trackUsage('view', data.id);

    if (window._paq) {
        window._paq.push(['setCustomUrl', '/partitions/' + data.id + '-' + data.slug]);
        window._paq.push(['setDocumentTitle', document.title]);
        window._paq.push(['trackPageView']);
    }
}

function _updateInfoPanel(data) {
    const fmt = (iso) => {
        if (!iso) return '—';
        return new Date(iso + 'Z').toLocaleDateString(lang() === 'en' ? 'en-US' : 'fr-FR', {
            day: 'numeric', month: 'long', year: 'numeric'
        });
    };
    const el = (id) => document.getElementById(id);
    el('partition-info-title').textContent   = data.title || 'Sans titre';
    el('partition-info-creator').textContent = data.creator_name || '—';
    el('partition-info-created').textContent = fmt(data.created_at);
    el('partition-info-updated').textContent = fmt(data.updated_at);

    const tuneEl = document.getElementById('partition-info-tune');
    if (tuneEl) {
        if (data.tune_id && data.tune_slug && data.tune_title) {
            tuneEl.innerHTML = '';
            const a = document.createElement('a');
            a.href        = '/tunes/' + data.tune_id + '-' + data.tune_slug;
            a.textContent = data.tune_title;
            a.style.color = 'var(--accent)';
            tuneEl.appendChild(a);
        } else {
            tuneEl.textContent = '—';
        }
    }

    const panel = document.getElementById('partition-info-panel');
    if (panel) panel.hidden = true;
}

function _requestEditAccess() {
    if (sessionToken) return;
    setPendingAction(() => {
        if (currentPartitionMeta?.is_owner) {
            refreshEditorAccess();
            document.querySelector('.abc-voice[contenteditable]')?.focus();
        }
    });
    openUserModal('user-login');
}

export function showAccessDenied() {
    const overlay = document.getElementById('modal-overlay');
    const content = document.getElementById('modal-content');
    if (!overlay || !content) return;
    const partitionsUrl = lang() === 'en' ? '/en/partitions' : '/partitions';
    content.innerHTML = `
        <div style="text-align:center;padding:32px 24px;">
            <div style="font-size:2.5em;margin-bottom:16px;">🔒</div>
            <p style="margin-bottom:24px;color:var(--text);">
                ${t('nav.access_denied_msg')}
            </p>
            <a href="${partitionsUrl}"
               style="display:inline-block;padding:10px 20px;
                      background:var(--accent);color:var(--darker);
                      border-radius:6px;text-decoration:none;text-transform:uppercase;
                      font-family:'Cinzel',serif;font-size:13px;font-weight:bold;">
                ${t('nav.access_denied_btn')}
            </a>
        </div>
    `;
    overlay.classList.add('active');
}

// Vrai propriétaire : un penn pupitre peut éditer une partition de son ensemble
// sans pouvoir la supprimer ni en changer la visibilité.
function _owns() {
    return currentPartitionMeta?.owns ?? currentPartitionMeta?.is_owner ?? false;
}

// ── UI droits propriétaire ────────────────────────────────────────────────────
export function updateOwnerUI() {
    const isOwner  = currentPartitionMeta?.is_owner  ?? false;
    const isPublic = currentPartitionMeta?.is_public ?? false;
    const owns     = _owns();

    // Éditeur réservé au propriétaire connecté, ou à une nouvelle partition.
    // La classe is-owner est posée par partition.php puis tenue à jour ici
    // (connexion, déconnexion, première sauvegarde).
    const canEdit = (isOwner && !!sessionToken) || !currentId;
    document.getElementById('view-editor')?.classList.toggle('is-owner', canEdit);

    const labelVis = document.getElementById('label-visibility');
    const chkVis   = document.getElementById('chk-visibility');
    const btnSave  = document.querySelector('.btn-save');

    if (labelVis) labelVis.style.display = owns ? 'flex' : 'none';
    if (chkVis)   chkVis.checked         = isPublic;

    const btnGroups = document.getElementById('btn-groups');
    if (btnGroups) btnGroups.style.display = (isOwner && currentId && sessionToken) ? '' : 'none';

    if (btnSave) btnSave.style.display = (isOwner || !currentId) ? '' : 'none';
}

// ── Actions propriétaire ──────────────────────────────────────────────────────
export async function deletePartition() {
    if (!currentId || !_owns()) return;
    const title = document.getElementById('partition-title-display').textContent;
    if (!confirm(t('nav.delete_confirm', { title }))) return;
    try {
        const res  = await fetch(`/api/partitions.php?id=${currentId}`, {
            method: 'DELETE', headers: authHeaders()
        });
        const data = await res.json();
        if (data.success) {
            window.location.href = lang() === 'en' ? '/en/partitions' : '/partitions';
        } else {
            alert(t('nav.delete_error', { error: data.error || t('nav.error_unknown') }));
        }
    } catch(e) { alert(t('nav.save_error_network')); }
}

export async function toggleVisibility() {
    if (!currentId || !_owns()) return;
    try {
        const res  = await fetch(`/api/partitions.php?id=${currentId}`, {
            method: 'PATCH', headers: authHeaders()
        });
        const data = await res.json();
        if (data.success) {
            setCurrentPartitionMeta({ ...currentPartitionMeta, is_public: data.is_public });
            updateOwnerUI();
        } else {
            alert(t('nav.toggle_error', { error: data.error || t('nav.save_error_server') }));
        }
    } catch(e) { alert(t('nav.save_error_network')); }
}

// ── Enregistrer ───────────────────────────────────────────────────────────────
// options.silent = true → pas de showSaveStatus (l'autosave gère son propre indicateur)
export async function savePartition({ silent = false } = {}) {
    if (!sessionToken) {
        setPendingAction(savePartition);
        openUserModal('user-login');
        return;
    }

    const content = getAbcContent();
    const m = content.match(/^T:\s*(.+)$/m);
    if (!m) {
        if (!silent) showSaveStatus(t('nav.save_error_title'), 'err');
        return;
    }

    const url  = currentId ? `/api/partitions.php?id=${currentId}` : `/api/partitions.php`;
    const body = { content };
    if (!currentId) {
        body.is_public = false;
        const tuneId = getPendingTuneId();
        if (tuneId) body.tune_id = tuneId;
    }

    try {
        const res = await fetch(url, {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify(body)
        });

        if (res.ok) {
            const data  = await res.json();
            const isNew = !currentId;
            setCurrentId(data.id);
            setCurrentSlug(data.slug);
            if (!currentPartitionMeta) setCurrentPartitionMeta({ is_owner: true, owns: true, is_public: false });
            document.getElementById('partition-title-display').textContent = data.title;

            clearPendingTuneId();
            await uploadAttachments(data.id);
            await refreshAttachButtons(data.id);

            // Marquer propre (stoppe le timer autosave)
            markClean();

            if (!silent) showSaveStatus(t('nav.save_ok'), 'ok');
            else         showSaveStatus(t('nav.autosave_ok'), 'ok');

            updateOwnerUI();

            if (isNew) {
                history.replaceState({}, '', '/partitions/' + data.id + '-' + data.slug);
            }

            generateAndSaveOgImage(data.id, sessionToken)
                .then(url => console.log('[og] succès:', url))
                .catch(e  => console.error('[og] erreur:', e));
            generateAndStorePdf(data.id, sessionToken, data.slug)
                .then(() => console.log('[pdf-store] succès'))
                .catch(e  => console.error('[pdf-store] erreur:', e));

        } else {
            const err = await res.json();
            if (!silent) showSaveStatus(err.error || t('nav.save_error_server'), 'err');
        }
    } catch(e) {
        if (!silent) showSaveStatus(t('nav.save_error_network'), 'err');
    }
}

export function showSaveStatus(msg, type) {
    const el = document.getElementById('save-status');
    el.textContent = msg;
    el.className   = 'save-status ' + (type || '');
    setTimeout(() => { el.textContent = ''; el.className = 'save-status'; }, 3000);
}